import has from 'lodash/has';
import get from 'lodash/get';
import set from 'lodash/set';

import { trackPayInFailure } from '../eventAnalytics';

import { getUserAccountDetails as getUserAccountDetailsApi } from '../../api/users';
import {
    INTERNAL_SERVER_ERROR,
    ARGUMENTS_ERROR,
    CARD_SECURITY_CODE_FIELD,
    PAYMENT_CARD_SECURITY_CODE,
} from '../../containers/checkout/constants';
import { getEventId } from '../../selectors/event';
import { getErrorMessage } from '../../utils/validation/getErrorMessage';
import { selectors as appSelectors } from '../../reducers/app';
import { PAYMENT_METHODS } from '../../constants';
import { setCanRetrySubmitAction } from '../order';
import { setReloadAfterCheckoutAction } from '../app';
import { CustomSubmissionError } from '@eventbrite/redux-form-tools';
import { gettext } from '@eventbrite/i18n';

export const checkIfUserWasLoggedIn = () => async (dispatch, getState) => {
    const {
        order: { email = null, userId = null },
    } = getState();

    if (userId && email && email.length) {
        const { response } = await getUserAccountDetailsApi(email);

        if (
            response &&
            has(response, 'can_login') &&
            response.can_login === false
        ) {
            return dispatch(setReloadAfterCheckoutAction(true));
        }
    }

    return null;
};

export const userIsStoringNewCreditCard = (formData) =>
    formData.paymentMethod === PAYMENT_METHODS.CREDIT &&
    formData.payment.vaultId === PAYMENT_METHODS.CREDIT &&
    formData.payment.saveCreditCard;

export const getPostMessageActionParamsFromState = (state) => ({
    eventId: getEventId(state),
    widgetParentUrl: appSelectors.getWidgetParentUrl(state.app),
});

export const handlePayInError = (dispatch, error) => {
    if (error && error.errors) {
        /** Both JavaScript errors and server errors from the API will end up being caught here.
         *  However, this block is intended specifically for gracefully handling errors from the
         *  API and passing them to redux-form to be translated into user-friendly notifications.
         *  Any JavaScript errors that show up here should be fixed and should not be passed on.
         */

        dispatch(trackPayInFailure(error.errors._error));

        const errorType = get(error, 'extraData.parsedError.error');

        switch (errorType) {
            case INTERNAL_SERVER_ERROR:
                // allow the user to retry if they got a 500 error
                dispatch(setCanRetrySubmitAction(true));
                break;
            case ARGUMENTS_ERROR:
                const argumentErrors = get(
                    error,
                    'extraData.parsedError.argumentErrors',
                );

                if (!argumentErrors) {
                    dispatch(setCanRetrySubmitAction(false));
                    throw error;
                }

                const argumentErrorKeys = Object.keys(argumentErrors);
                const mappedErrors = {};
                argumentErrorKeys.forEach((field) => {
                    switch (field) {
                        case 'source_instrument.cvv':
                            set(
                                mappedErrors,
                                PAYMENT_CARD_SECURITY_CODE,
                                getErrorMessage(CARD_SECURITY_CODE_FIELD),
                            );
                    }
                });

                throw new CustomSubmissionError(
                    {
                        ...mappedErrors,
                        _error: gettext(
                            'Please correct the highlighted fields below',
                        ),
                    },
                    {},
                );
            default:
                dispatch(setCanRetrySubmitAction(false));
        }
        throw error;
    }
};

/**
 * Returns true if the user has chosen to use CREDIT payment method,
 * then chose a card stored in their account.
 *
 * Note: payment.vaultId === CREDIT if the user chose to add a new card.
 */
export const userSelectedStoredPaymentMethod = (formData) =>
    formData.paymentMethod === PAYMENT_METHODS.CREDIT &&
    formData.payment.vaultId &&
    formData.payment.vaultId !== PAYMENT_METHODS.CREDIT;

/**
 * Returns true when payment method type is credit card or is falsy (since we default to credit card).
 *
 * @param {string} paymentMethodType
 * @returns {boolean}
 */
const isCreditCardPaymentMethod = (paymentMethodType) =>
    !paymentMethodType || paymentMethodType === PAYMENT_METHODS.CREDIT;

/**
 * Returns true if the user has chosen to use CREDIT payment method,
 * then chose to add a new card stored in their account.
 *
 * Note: payment.vaultId === CREDIT if the user chose to add a new card.
 */
export const userSelectedNewCreditCardPaymentMethod = (formData) =>
    isCreditCardPaymentMethod(formData.paymentMethod) &&
    formData.payment.vaultId &&
    formData.payment.vaultId === PAYMENT_METHODS.CREDIT;
